import { Paginator } from 'array-paginator';
import { defineStore } from 'pinia';
import { ResolveListForExportCallback } from 'src/stores/_infrastructure/ResolveListForExportCallback';
import { completeListExportViewFor } from 'src/stores/_infrastructure/completeListExportView';
import { resolveDesktopModuleDateFilterForQuery } from 'src/stores/_infrastructure/dateTimeRangeQueryValue';
import { dedupByAggregateId } from 'src/stores/_infrastructure/dedupByAggregateId';
import {
	COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
	prepareQueryForCompleteListExport,
} from 'src/stores/_infrastructure/prepareQueryForCompleteListExport';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { GetAllUsersWithAccessToTheModuleForTheUserFilterQuery } from 'src/stores/genericActivityList/queries';
import { CopyTheMailToTheDestinationAccountCommand } from 'src/stores/mailList/commands/CopyTheMailToTheDestinationAccountCommand';
import {
	MAIL_LIST_GENERIC_ACTIVITY_LIST_SORT,
	MAIL_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
} from 'src/stores/mailList/localStorageKeys';
import {
	GetTheMailModuleBoxesForTheUserQuery,
	GetTheMailModuleMailsForTheUserQuery,
} from 'src/stores/mailList/queries';
import createMailListState from 'src/stores/mailList/state';
import { TSMap } from 'typescript-map';

import { DateRangeSelection } from 'src/models/common';
import { FilterBoxes, FilterBoxesRunType, FilterUsers, FilterUsersRunType } from 'src/models/filter';
import {
	ActivityListTypeEnum,
	GenericActivityListPagination,
	GenericActivityListSort,
} from 'src/models/genericActivityList';
import { MailListItem, MailListItemRunType } from 'src/models/mailList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { ModuleNameEnum } from 'src/models/module/enums';
import { SerializedQuery } from 'src/models/query';
import { StatusEnum } from 'src/models/status/enums';

import {
	removeTheAggregateIdsFromTheCollection,
	setTheStateForTheAggregateIds,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { createCompareFunction } from 'src/util/collection/customCompareFunction';
import { DEFAULT_LIST_INSTANCE_ID, deriveListStoreId, isDefaultInstance } from 'src/util/listInstance/listInstanceKeys';

import {
	GetAllUsersWithAccessToTheModuleForTheUserFilterMailListCallBack,
	GetTheAllMailModuleMailsForTheUserCallBack,
	GetTheMailModuleBoxesForTheUserQueryCallBack,
	GetTheMineMailModuleMailsForTheUserCallBack,
	GetTheOpenMailModuleMailsForTheUserCallBack,
} from './callbacks';
import {
	CompleteListExportFetchPayload,
	CopyMailPayload,
	DeleteSelectedAggregateIdsPayload,
	ListFetchPayload,
	QueryStorePayload,
	SetDateFilterPayload,
	SetDateTimeRangeFilterPayload,
	SetDirectionFilterPayload,
	SetFilteredBoxesPayload,
	SetFilteredUsersPayload,
	SetListPayload,
	SetListStatePayload,
	SetPaginationPagePayload,
	SetPaginationPayload,
	SetSelectedSharedQueryPayload,
	SetSortPayload,
} from './interfaces';

const DEFAULT_ROWS_PER_PAGE = 100;
const DEFAULT_LIST_SORT: GenericActivityListSort = {
	direction: 'DESC',
	property: 'timestamp',
};

const createDefaultPaginationEntry = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: DEFAULT_ROWS_PER_PAGE,
});

const createDefaultPaginationMap = (): TSMap<ActivityListTypeEnum, GenericActivityListPagination> =>
	new TSMap<ActivityListTypeEnum, GenericActivityListPagination>([
		[ActivityListTypeEnum.ALL, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.MINE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.OPEN, createDefaultPaginationEntry()],
	]);

const replaceActiveQueryStoreSubscription = <T>({
	matchesLogicalView,
	nextSerializedQuery,
	parseQuery,
	queryStore,
	subscriptions,
}: {
	matchesLogicalView: (serializedQuery: SerializedQuery) => boolean;
	nextSerializedQuery: SerializedQuery;
	parseQuery: (serializedQuery: SerializedQuery) => T;
	queryStore: ListFetchPayload['queryStore'];
	subscriptions: SerializedQuery[];
}): SerializedQuery[] => {
	const staleSubscriptions = subscriptions.filter(matchesLogicalView);

	staleSubscriptions.forEach((serializedQuery) => {
		void queryStore?.unsubscribeQuery(parseQuery(serializedQuery));
	});

	return [...subscriptions.filter((serializedQuery) => !matchesLogicalView(serializedQuery)), nextSerializedQuery];
};

// Task 3.1f: isolierte gespeicherte-Listen-Tabs (`$id !== 'mailList'`) sollen view-scoped Zähler/Zeilen zeigen —
// deshalb unterdrücken ihre Query-Konstruktionen die Box→Custom-Kopplung (sonst kollabieren Offen/Meine/Alle auf
// dieselbe Custom-Query → 8/8/8). Der Default-Store behält die klassische Kopplung (byte-identisch). Wird an ALLE
// Konstruktionsstellen (Dispatch + Register + Export) gereicht, damit Subscription-Identifier konsistent bleiben.
const suppressBoxCustomCouplingForInstance = (storeId: string): boolean => storeId !== 'mailList';

const defineMailListStore = (id: string) =>
	defineStore(id, {
		actions: {
			addQueryStoreSubscription(serializedQuery: SerializedQuery): void {
				this.queryStoreSubscriptions.push(serializedQuery);
			},
			clearQueryStoreSubscriptions(): void {
				this.queryStoreSubscriptions = [];
			},
			clearSubscriptions(payload: QueryStorePayload): void {
				if (payload.queryStore !== undefined) {
					this.queryStoreSubscriptions.forEach((serializedQuery: SerializedQuery) => {
						switch (serializedQuery.type) {
							case 'GetTheMailModuleMailsForTheUserQuery':
								void payload.queryStore?.unsubscribeQuery(
									GetTheMailModuleMailsForTheUserQuery.fromJSON<GetTheMailModuleMailsForTheUserQuery>(
										serializedQuery.query
									)
								);
								break;
							default:
								break;
						}
					});
				}

				this.clearQueryStoreSubscriptions();
			},
			copyTheMailToTheDestinationAccount(payload: CopyMailPayload): void {
				void payload.commandBus?.sendCommand(
					new CopyTheMailToTheDestinationAccountCommand(payload.aggregateId, payload.destinationAccountId)
				);
			},
			deleteSelectedAggregateIds(payload: DeleteSelectedAggregateIdsPayload): void {
				const repaginateAndSet = (
					mailList: MailListItem[],
					totalCount: number,
					applyPage: (pagePayload: SetListPayload) => void
				): void => {
					const mailListCopy = removeTheAggregateIdsFromTheCollection(
						mailList as unknown as never[],
						payload.aggregateIds
					) as unknown as MailListItem[];

					if (mailListCopy !== mailList) {
						const mailListCopyPaginator = new Paginator(mailListCopy, DEFAULT_ROWS_PER_PAGE, 1);

						while (mailListCopyPaginator.current <= mailListCopyPaginator.total) {
							applyPage({
								page: mailListCopyPaginator.current,
								payload: mailListCopyPaginator.page(mailListCopyPaginator.current) as MailListItem[],
								totalCount: totalCount,
							});

							mailListCopyPaginator.next();
						}
					}
				};

				repaginateAndSet(this.mailListOpen, this.mailListOpenTotalCount, (listPayload) =>
					this.setOpenMailList(listPayload)
				);
				repaginateAndSet(this.mailListAll, this.mailListAllTotalCount, (listPayload) =>
					this.setAllMailList(listPayload)
				);
				repaginateAndSet(this.mailListMine, this.mailListMineTotalCount, (listPayload) =>
					this.setMineMailList(listPayload)
				);
			},
			async fetchAllMailList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setAllMailList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchAllMailList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.ALL);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const page = pagination.page;
				const query = new GetTheMailModuleMailsForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.ALL, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.ALL, queryIdentifier);

				const callback = new GetTheAllMailModuleMailsForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<MailListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: MailListItemRunType,
				});
			},
			async fetchFilterBoxes(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterBoxes([]);
					return;
				}

				await payload.queryStore.query<Array<FilterBoxes>>({
					callback: new GetTheMailModuleBoxesForTheUserQueryCallBack(this),
					createTime: new Date(),
					query: new GetTheMailModuleBoxesForTheUserQuery(),
					responseType: 'json',
					runType: FilterBoxesRunType,
				});
			},
			async fetchFilterUsers(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterUsers([]);
					return;
				}

				await payload.queryStore.query<Array<FilterUsers>>({
					callback: new GetAllUsersWithAccessToTheModuleForTheUserFilterMailListCallBack(this),
					createTime: new Date(),
					query: new GetAllUsersWithAccessToTheModuleForTheUserFilterQuery(ModuleNameEnum.MODULE_EMAIL),
					responseType: 'json',
					runType: FilterUsersRunType,
				});
			},
			async fetchMineMailList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setMineMailList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchMineMailList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.MINE);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const page = pagination.page;
				const query = new GetTheMailModuleMailsForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.MINE, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.MINE, queryIdentifier);

				const callback = new GetTheMineMailModuleMailsForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<MailListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: MailListItemRunType,
				});
			},
			async fetchOpenMailList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setOpenMailList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchOpenMailList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.OPEN);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const page = pagination.page;
				const query = new GetTheMailModuleMailsForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.OPEN, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.OPEN, queryIdentifier);

				const callback = new GetTheOpenMailModuleMailsForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<MailListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: MailListItemRunType,
				});
			},
			async fetchTheCompleteListForExport(
				payload: CompleteListExportFetchPayload
			): Promise<MailListItem[] | null> {
				if (payload.queryStore === undefined) {
					return null;
				}

				const totalCountByListType: Partial<Record<ActivityListTypeEnum, number>> = {
					[ActivityListTypeEnum.ALL]: this.mailListAllTotalCount,
					[ActivityListTypeEnum.MINE]: this.mailListMineTotalCount,
					[ActivityListTypeEnum.OPEN]: this.mailListOpenTotalCount,
				};
				const pagination: GenericActivityListPagination = {
					page: 1,
					rowsPerPage:
						(totalCountByListType[payload.activityListType] ?? 0) + COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
				};
				const query = prepareQueryForCompleteListExport(
					new GetTheMailModuleMailsForTheUserQuery(
						completeListExportViewFor(payload.activityListType),
						resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
						this.filteredBoxIds,
						this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
						payload.searchTerm,
						payload.hiddenSearchTerm,
						this.directionFilter,
						pagination,
						this.sortFor(payload.activityListType),
						suppressBoxCustomCouplingForInstance(this.$id)
					)
				);

				let completeList: MailListItem[] | null = null;

				await payload.queryStore.query<MailListItem[]>({
					callback: new ResolveListForExportCallback<MailListItem>((list) => {
						completeList = list;
					}),
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: MailListItemRunType,
				});

				return completeList;
			},
			isLatestQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): boolean {
				return (this.activeQueryIdentifierByListType.get(activityListType) ?? '') === queryIdentifier;
			},
			registerUnsubscribeFetchAllMailList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const query = new GetTheMailModuleMailsForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					this.sortFor(ActivityListTypeEnum.ALL),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceMailListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchMineMailList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const query = new GetTheMailModuleMailsForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					this.sortFor(ActivityListTypeEnum.MINE),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceMailListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchOpenMailList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const query = new GetTheMailModuleMailsForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					this.directionFilter,
					pagination,
					this.sortFor(ActivityListTypeEnum.OPEN),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceMailListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			replaceMailListQueryStoreSubscription(payload: {
				query: GetTheMailModuleMailsForTheUserQuery;
				queryStore: ListFetchPayload['queryStore'];
			}): void {
				const nextSerializedQuery: SerializedQuery = {
					query: JSON.stringify(payload.query),
					type: 'GetTheMailModuleMailsForTheUserQuery',
				};
				const expectedStandardFilterSelection = payload.query.standardFilterSelection;

				this.queryStoreSubscriptions = replaceActiveQueryStoreSubscription({
					matchesLogicalView: (serializedQuery) => {
						if (serializedQuery.type !== nextSerializedQuery.type) {
							return false;
						}

						return (
							GetTheMailModuleMailsForTheUserQuery.fromJSON<GetTheMailModuleMailsForTheUserQuery>(
								serializedQuery.query
							).standardFilterSelection === expectedStandardFilterSelection
						);
					},
					nextSerializedQuery,
					parseQuery: (serializedQuery) =>
						GetTheMailModuleMailsForTheUserQuery.fromJSON<GetTheMailModuleMailsForTheUserQuery>(
							serializedQuery.query
						),
					queryStore: payload.queryStore,
					subscriptions: this.queryStoreSubscriptions,
				});
			},
			reset(payload: QueryStorePayload): void {
				this.clearSubscriptions({
					queryStore: payload.queryStore,
				});
				this.resetState();
			},
			resetState(): void {
				const today = new Date().toISOString().split('T')[0];
				this.activeQueryIdentifierByListType = new TSMap<ActivityListTypeEnum, string>([
					[ActivityListTypeEnum.ALL, ''],
					[ActivityListTypeEnum.MINE, ''],
					[ActivityListTypeEnum.OPEN, ''],
				]);
				this.dateFilter = today;
				this.today = today;
				this.dateTimeRangeFilter = PreconfiguredTimeRanges.TODAY;
				this.directionFilter = 'combined';
				this.filteredUsers = [];
				this.filteredBoxIds = [];
				this.selectedSharedQueryId = '';
				this.queryStoreSubscriptions = [];
				this.mailListOpenTotalCount = 0;
				this.mailListOpen = [];
				this.mailListOpenSliced.clear();
				this.mailListAllTotalCount = 0;
				this.mailListAll = [];
				this.mailListAllSliced.clear();
				this.mailListMineTotalCount = 0;
				this.mailListMine = [];
				this.mailListMineSliced.clear();
				this.pagination = createDefaultPaginationMap();
			},
			setActiveQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): void {
				this.activeQueryIdentifierByListType.set(activityListType, queryIdentifier);
			},
			setAllMailList(payload: SetListPayload): void {
				this.mailListAllTotalCount = payload.totalCount;
				this.mailListAllSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				this.mailListAll = dedupByAggregateId(
					[...this.mailListAllSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<MailListItem>(sort.property as keyof MailListItem, sort.direction));
			},
			setDateFilterAction(payload: SetDateFilterPayload): void {
				this.setDateFilterValue(payload.dateFilter);
			},
			setDateFilterValue(payload: DateRangeSelection | string): void {
				this.dateFilter = payload || '';
			},
			setDateTimeRangeFilter(payload: SetDateTimeRangeFilterPayload): void {
				this.setDateTimeRangeFilterValue(payload.dateTimeRangeFilter);
			},
			setDateTimeRangeFilterValue(payload: PreconfiguredTimeRanges): void {
				this.dateTimeRangeFilter = payload;
			},
			setDirectionFilterAction(payload: SetDirectionFilterPayload): void {
				this.setDirectionFilterValue(payload.directionFilter);
			},
			setDirectionFilterValue(payload: 'combined' | 'in' | 'out'): void {
				this.directionFilter = payload;
			},
			setFilterBoxes(payload: Array<FilterBoxes>): void {
				this.filterBoxes = payload || [];
			},
			setFilterUsers(payload: Array<FilterUsers>): void {
				this.filterUsers = payload || [];
			},
			setFilteredBoxes(payload: Array<string>): void {
				this.filteredBoxIds = payload || [];
			},
			setFilteredBoxesAction(payload: SetFilteredBoxesPayload): void {
				this.setFilteredBoxes(payload.boxIdFilter);
			},
			setFilteredUsers(payload: Array<FilterUsers>): void {
				this.filteredUsers = payload || [];
			},
			setFilteredUsersAction(payload: SetFilteredUsersPayload): void {
				this.setFilteredUsers(payload.filterUsers);
			},
			setListLoadError(): void {
				const genericActivityListStore = useGenericActivityListStore();
				genericActivityListStore.setListLoadError();
			},
			setListViewModeLarge(payload: boolean): void {
				if (this.$id === 'mailList') {
					localStorage.setItem(MAIL_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE, JSON.stringify(payload));
				}
				this.listViewModeLarge = payload;
			},
			setMineMailList(payload: SetListPayload): void {
				this.mailListMineTotalCount = payload.totalCount;
				this.mailListMineSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				this.mailListMine = dedupByAggregateId(
					[...this.mailListMineSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<MailListItem>(sort.property as keyof MailListItem, sort.direction));
			},
			setOpenMailList(payload: SetListPayload): void {
				this.mailListOpenTotalCount = payload.totalCount;
				this.mailListOpenSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				this.mailListOpen = dedupByAggregateId(
					[...this.mailListOpenSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<MailListItem>(sort.property as keyof MailListItem, sort.direction));
			},
			setPagination(payload: SetPaginationPayload): void {
				this.pagination.set(payload.activityListType, payload.pagination);
			},
			setPaginationAction(payload: SetPaginationPayload): void {
				this.setPagination(payload);
			},
			setPaginationPage(payload: SetPaginationPagePayload): void {
				const pagination = this.paginationFor(payload.activityListType);
				pagination.page = payload.page;
				this.pagination.set(payload.activityListType, pagination);
			},
			setPaginationPageAction(payload: SetPaginationPagePayload): void {
				this.setPaginationPage(payload);
			},
			setSelectedSharedQuery(payload: string): void {
				this.selectedSharedQueryId = payload || '';
			},
			setSelectedSharedQueryAction(payload: SetSelectedSharedQueryPayload): void {
				this.setSelectedSharedQuery(payload.selectedSharedQuery ?? '');
			},
			setSort(payload: SetSortPayload): void {
				this.sort.set(payload.activityListType, payload.sort);
			},
			setSortAction(payload: SetSortPayload): void {
				this.setSort(payload);
				if (this.$id === 'mailList') {
					localStorage.setItem(MAIL_LIST_GENERIC_ACTIVITY_LIST_SORT, JSON.stringify(this.sort.toJSON()));
				}
			},
			setTheMailStateToReady(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.READY);
			},
			setTheMailStateToSeen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.SEEN);
			},
			setTheMailStateToUnseen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.UNSEEN);
			},
			setTheStateForAggregateIds(aggregateIds: string[], status: StatusEnum): void {
				let openCountDelta = 0;
				aggregateIds.forEach((aggregateId) => {
					const known =
						this.mailListOpen.find((item) => item.aggregateId === aggregateId) ??
						this.mailListAll.find((item) => item.aggregateId === aggregateId) ??
						this.mailListMine.find((item) => item.aggregateId === aggregateId);
					if (!known) {
						return;
					}
					const wasReady = known.statusButton.status === StatusEnum.READY;
					const willBeReady = status === StatusEnum.READY;
					if (!wasReady && willBeReady) {
						openCountDelta -= 1;
					} else if (wasReady && !willBeReady) {
						openCountDelta += 1;
					}
				});
				if (openCountDelta !== 0) {
					this.mailListOpenTotalCount = Math.max(0, this.mailListOpenTotalCount + openCountDelta);
				}

				const setPagedList = <T>(
					entries: T[],
					totalCount: number,
					applyPage: (payload: { page: number; payload: T[]; totalCount: number }) => void
				): void => {
					if (entries.length === 0) {
						return;
					}

					const paginator = new Paginator(entries, DEFAULT_ROWS_PER_PAGE, 1);
					while (paginator.current <= paginator.total) {
						applyPage({
							page: paginator.current,
							payload: paginator.page(paginator.current) as T[],
							totalCount: totalCount,
						});
						paginator.next();
					}
				};

				setPagedList<MailListItem>(
					setTheStateForTheAggregateIds(
						this.mailListOpen as never[],
						aggregateIds,
						status
					) as unknown as MailListItem[],
					this.mailListOpenTotalCount,
					(payload) => this.setOpenMailList(payload)
				);
				setPagedList<MailListItem>(
					setTheStateForTheAggregateIds(
						this.mailListAll as never[],
						aggregateIds,
						status
					) as unknown as MailListItem[],
					this.mailListAllTotalCount,
					(payload) => this.setAllMailList(payload)
				);
				setPagedList<MailListItem>(
					setTheStateForTheAggregateIds(
						this.mailListMine as never[],
						aggregateIds,
						status
					) as unknown as MailListItem[],
					this.mailListMineTotalCount,
					(payload) => this.setMineMailList(payload)
				);
			},
			setToday(payload: string): void {
				this.today = payload || '';
			},
			silentSetAllMailList(): void {
				this.mailListAllTotalCount = 0;
				this.mailListAll = [];
				this.mailListAllSliced.clear();
				this.pagination.set(ActivityListTypeEnum.ALL, createDefaultPaginationEntry());
			},
			silentSetMineMailList(): void {
				this.mailListMineTotalCount = 0;
				this.mailListMine = [];
				this.mailListMineSliced.clear();
				this.pagination.set(ActivityListTypeEnum.MINE, createDefaultPaginationEntry());
			},
			silentSetOpenMailList(): void {
				this.mailListOpenTotalCount = 0;
				this.mailListOpen = [];
				this.mailListOpenSliced.clear();
				this.pagination.set(ActivityListTypeEnum.OPEN, createDefaultPaginationEntry());
			},
			spamSelectedAggregateIds(payload: DeleteSelectedAggregateIdsPayload): void {
				this.deleteSelectedAggregateIds(payload);
			},
		},
		getters: {
			paginationFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListPagination {
				return (activityListType: ActivityListTypeEnum): GenericActivityListPagination =>
					state.pagination.get(activityListType) ?? createDefaultPaginationEntry();
			},
			sortFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListSort {
				return (activityListType: ActivityListTypeEnum): GenericActivityListSort =>
					state.sort.get(activityListType) ?? { ...DEFAULT_LIST_SORT };
			},
		},
		state: createMailListState,
	});

export const useMailListStore = defineMailListStore('mailList');

const mailListInstanceDefinitions = new Map<string, ReturnType<typeof defineMailListStore>>();

export const createMailListStore = (instanceId: string = DEFAULT_LIST_INSTANCE_ID) => {
	if (isDefaultInstance(instanceId)) {
		return useMailListStore();
	}

	const storeId = deriveListStoreId('mailList', instanceId);
	let definition = mailListInstanceDefinitions.get(storeId);

	if (definition === undefined) {
		definition = defineMailListStore(storeId);
		mailListInstanceDefinitions.set(storeId, definition);
	}

	return definition();
};

export * from './interfaces';
export * from './types';
